import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import SvgIcon from '@mui/material/SvgIcon';

import { RouterLink } from 'src/components/router-link';

const getItemSx = (depth, active, offset) => ({
  alignItems: 'center',
  borderRadius: 2,
  display: 'flex',
  justifyContent: 'flex-start',
  minHeight: depth === 0 ? 42 : 36,
  mx: 0.5,
  pl: depth === 0 ? 1 : `${10 + offset}px`,
  pr: 1.25,
  py: 0.75,
  textAlign: 'left',
  transition: 'background-color 0.2s ease',
  width: 'calc(100% - 8px)',
  ...(active && depth === 0 && {
    bgcolor: 'var(--nav-item-active-bg)',
    '& .nav-item-icon-wrap': {
      bgcolor: 'var(--nav-item-icon-active-bg)',
      color: 'var(--nav-item-icon-active-color)'
    },
    '& .nav-item-title': {
      color: 'var(--nav-item-active-color)',
      fontWeight: 600
    }
  }),
  '&:hover': {
    bgcolor: 'var(--nav-item-hover-bg)'
  }
});

const IconWrap = ({ active, children, depth }) => {
  if (depth > 0) {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', height: 20, justifyContent: 'center', mr: 1, width: 20 }}>
        <Box
          sx={{
            bgcolor: active ? 'var(--nav-item-icon-active-color)' : 'var(--nav-item-icon-color)',
            borderRadius: '50%',
            height: active ? 6 : 4,
            opacity: active ? 1 : 0.45,
            width: active ? 6 : 4
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      className="nav-item-icon-wrap"
      sx={{
        alignItems: 'center',
        borderRadius: 1.5,
        color: 'var(--nav-item-icon-color)',
        display: 'inline-flex',
        flexShrink: 0,
        height: 32,
        justifyContent: 'center',
        mr: 1.25,
        transition: 'background-color 0.2s ease, color 0.2s ease',
        width: 32
      }}
    >
      {children}
    </Box>
  );
};

IconWrap.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number
};

const Title = ({ active, depth, disabled, title }) => (
  <Box
    className="nav-item-title"
    component="span"
    sx={{
      color: 'var(--nav-item-color)',
      flexGrow: 1,
      fontSize: depth > 0 ? '0.8125rem' : '0.875rem',
      fontWeight: 500,
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
      ...(active && { color: 'var(--nav-item-active-color)' }),
      ...(disabled && { color: 'var(--nav-item-disabled-color)' })
    }}
  >
    {title}
  </Box>
);

Title.propTypes = {
  active: PropTypes.bool,
  depth: PropTypes.number,
  disabled: PropTypes.bool,
  title: PropTypes.string.isRequired
};

export const SideNavItem = (props) => {
  const {
    active,
    children,
    depth = 0,
    disabled,
    external,
    icon,
    label,
    open: openProp,
    path,
    title
  } = props;
  const [open, setOpen] = useState(!!openProp);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const offset = depth === 0 ? 0 : (depth - 1) * 14;

  if (children) {
    return (
      <li>
        <ButtonBase disabled={disabled} onClick={handleToggle} sx={getItemSx(depth, active, offset)}>
          {icon && <IconWrap active={active} depth={depth}>{icon}</IconWrap>}
          <Title active={active} depth={depth} disabled={disabled} title={title} />
          <SvgIcon sx={{ color: 'var(--nav-item-chevron-color)', fontSize: 16, ml: 0.5 }}>
            {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </SvgIcon>
        </ButtonBase>
        <Collapse in={open} sx={{ mt: 0.25 }}>
          {children}
        </Collapse>
      </li>
    );
  }

  const linkProps = path
    ? external
      ? { component: 'a', href: path, target: '_blank' }
      : { component: RouterLink, href: path }
    : {};

  return (
    <li>
      <ButtonBase disabled={disabled} sx={getItemSx(depth, active, offset)} {...linkProps}>
        {icon && <IconWrap active={active} depth={depth}>{icon}</IconWrap>}
        <Title active={active} depth={depth} disabled={disabled} title={title} />
        {label && <Box component="span" sx={{ ml: 1 }}>{label}</Box>}
      </ButtonBase>
    </li>
  );
};

SideNavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number,
  disabled: PropTypes.bool,
  external: PropTypes.bool,
  icon: PropTypes.node,
  label: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired
};
